import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import {
  getGetAutomationQueryKey,
  getListAutomationsQueryKey,
  getQueryAutomationQueryKey,
  getListAutomationsInfiniteQueryKey,
  getListAutomationSummariesQueryKey,
  getListAutomationSummariesInfiniteQueryKey,
  useDeleteAutomation,
} from '@/generated/hooks/grimoire/v1/api';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { AssetDeleteDialog } from '@/layouts/AssetCatalogLayout';

interface IAutomationDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  automationId: string;
  onDelete?: () => void;
}

export function AutomationDeleteDialog({
  open,
  onClose,
  title,
  automationId,
  onDelete,
}: IAutomationDeleteDialogProps) {
  const { t: tAutomation } = useTranslation('automation');
  const queryClient = useQueryClient();

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const {
    mutate: deleteAutomation,
    isPending: isDeleteAutomationPending,
    error: deleteAutomationError,
  } = useDeleteAutomation();

  const handleDelete = () => {
    deleteAutomation(
      {
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: automationId,
        params: {
          force: true,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getListAutomationsQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationsInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          // Marked stale without refetching: the open editor would 404 before it closes.
          for (const queryKey of [
            getGetAutomationQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              automationId,
            ),
            getQueryAutomationQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              automationId,
            ),
          ]) {
            queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
          }
          onClose();
          onDelete?.();
        },
      },
    );
  };

  return (
    <AssetDeleteDialog
      open={open}
      onClose={onClose}
      onConfirm={handleDelete}
      title={tAutomation('automation.deleteConfirmation.title')}
      description={tAutomation('automation.deleteConfirmation.description', {
        processTitle: title,
      })}
      confirmation={tAutomation('automation.deleteConfirmation.description2')}
      confirmLabel={tAutomation('automation.deleteConfirmation.actions.delete')}
      isPending={isDeleteAutomationPending}
      error={deleteAutomationError}
      errorMessage={tAutomation('automation.deleteConfirmation.error')}
      stopPropagation
      data-testid="automation-delete-dialog"
    />
  );
}
